import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { BrandingEditor, type BrandingToolModal } from "@/components/branding";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Button } from "@/components/ui/button";
import { getRandomGenieDefaultImage } from "@/data/genie/defaults";
import { useFixSignatureContacts, useUpdateAgent } from "@/hooks/agent/useAgents";
import { emailTemplateService } from "@/services/communication/EmailTemplateService";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING, GenieBranding } from "@/types/ui/branding";
import { extractColorPalette, suggestTextColors } from "@/utils/image/colorExtraction";

interface BrandingModalProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  isLocked?: boolean;
  isPreview?: boolean; // Preview mode shows example without lock overlay
  onBrandingChange?: (branding: GenieBranding) => void; // Setup mode: pass changes to parent
  onSwitchModal?: (modal: BrandingToolModal) => void; // Lets the "More" tab hand off to the QR/Web Embed editors
}

const normalizeBranding = (branding?: GenieBranding | null): GenieBranding => {
  // A genie that has never saved a signatureStyle has no signature anywhere —
  // not in HG Mail's reply footer, not in any report email — regardless of
  // DEFAULT_SIGNATURE_STYLE.enabled being true (that default only takes effect
  // once a config is actually persisted, via BrandingModal's save or the
  // send-time self-heal). Showing the toggle "on" here would say otherwise, so
  // an unconfigured genie is normalized to enabled: false — an honest reflection
  // of "nothing saved yet" rather than a default this UI hasn't earned.
  const hasSavedSignatureStyle = Boolean(branding?.signatureStyle);

  return {
    ...DEFAULT_BRANDING,
    ...branding,
    qrCodeStyle: {
      ...(DEFAULT_BRANDING.qrCodeStyle || {}),
      ...(branding?.qrCodeStyle || {}),
    },
    embedStyle: {
      ...(DEFAULT_BRANDING.embedStyle || {}),
      ...(branding?.embedStyle || {}),
    },
    signatureStyle: {
      ...DEFAULT_BRANDING.signatureStyle!,
      ...(branding?.signatureStyle || {}),
      enabled: hasSavedSignatureStyle
        ? (branding?.signatureStyle?.enabled ?? DEFAULT_BRANDING.signatureStyle!.enabled)
        : false,
    },
  };
};

const BrandingModal = ({
  genie,
  isOpen,
  onClose,
  isLocked = false,
  isPreview = false,
  onBrandingChange,
  onSwitchModal,
}: BrandingModalProps) => {
  // Callers remount this component (via a key toggling on isOpen) each time
  // the drawer opens for a genie, so this initializer alone provides a fresh
  // draft from the current genie.branding on every open — no reset-on-prop-
  // change logic needed. Local state is otherwise intentionally decoupled
  // from genie.branding while mounted: it's an edit draft, and background
  // refetches (e.g. from Fix or Save) must not clobber in-progress edits.
  const [branding, setBranding] = useState<GenieBranding>(normalizeBranding(genie.branding));
  const [activeTab, setActiveTab] = useState("images");

  const updateAgent = useUpdateAgent();
  const fixSignatureContacts = useFixSignatureContacts();

  // Determine if we're in setup mode (agent doesn't exist yet)
  const isSetupMode = !genie.id || genie.id === "temp";

  // Calls the same self-heal endpoint HG Mail relies on (agents/signature-style)
  // to (re)snapshot phoneNumber/liveUrl from this genie's live phone number and
  // url_name straight into signatureStyle server-side. Only touches those two
  // fields — every other signatureStyle preference is left exactly as saved.
  const handleFixSignatureContacts = () => {
    if (isSetupMode) return;
    fixSignatureContacts.mutate(
      { agent: genie },
      {
        onSuccess: (healedAgent) => {
          setBranding(normalizeBranding(healedAgent.branding));
          toast.success("Signature link and phone number refreshed.");
        },
        onError: (error) => {
          console.error("Failed to refresh signature contact info:", error);
          toast.error("Failed to refresh signature link/phone");
        },
      },
    );
  };

  const handleSave = async () => {
    // Setup mode: pass branding changes to parent component
    if (isSetupMode && onBrandingChange) {
      onBrandingChange(branding);
      toast.success("Branding updated! Create your genie to save these changes.");
      onClose();
      return;
    }

    // Edit mode: update existing agent in database
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: { branding },
      });

      // Keep this genie's own report-email "Email Signature" block toggles in
      // sync with the switch the user just flipped here — only when it actually
      // changed, and only for this genie's templates (never the shared
      // user-level template other genies may use).
      const previouslyEnabled = Boolean(genie.branding?.signatureStyle) && genie.branding.signatureStyle.enabled;
      if (branding.signatureStyle!.enabled !== previouslyEnabled) {
        try {
          await emailTemplateService.syncSignatureEnabled(genie.id, branding.signatureStyle!.enabled);
        } catch (syncError) {
          console.error("Failed to sync signature block across email templates:", syncError);
        }
      }

      toast.success("Branding updated successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to update branding:", error);
      toast.error("Failed to update branding");
    }
  };

  const handleResetToDefault = async () => {
    if (
      !confirm(
        "Are you sure you want to reset to default branding? This will pick a new random avatar and extract colors from it, just like when you first created this genie.",
      )
    ) {
      return;
    }

    try {
      toast.info("Resetting to default branding...");

      // Get a random default image (same as genie creation)
      const defaultImage = getRandomGenieDefaultImage();

      // Start with base default branding
      let newBranding = {
        ...DEFAULT_BRANDING,
        logoUrl: defaultImage,
        customAvatarUrl: defaultImage,
      };

      // Extract colors from the default image
      try {
        const { suggestedPrimary, suggestedSecondary, palette, dominantColor } =
          await extractColorPalette(defaultImage);
        const { primaryText } = suggestTextColors(suggestedPrimary);

        // Apply extracted colors to ALL branding properties
        newBranding = {
          ...newBranding,
          primaryColor: suggestedPrimary,
          secondaryColor: suggestedSecondary,
          gradientEnabled: true,
          textColor: primaryText,
          extractedPalette: {
            dominantColor,
            palette,
            suggestedPrimary,
            suggestedSecondary,
            isLight: false, // will be calculated in extraction
          },
          qrCodeStyle: {
            ...DEFAULT_BRANDING.qrCodeStyle!,
            foregroundColor: suggestedPrimary,
          },
          embedStyle: {
            ...DEFAULT_BRANDING.embedStyle!,
            headerColor: suggestedPrimary,
          },
        };
      } catch (err) {
        console.warn("Color extraction failed during reset, using default colors:", err);
      }

      setBranding(newBranding);
      toast.success("Branding reset to default with new avatar! Remember to save your changes.");
    } catch (error) {
      console.error("Failed to reset branding:", error);
      toast.error("Failed to reset branding");
    }
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={isPreview ? `${genie.name} — Example Branding` : `Customize ${genie.name}'s Branding`}
      description="Adjust images, colors, and email signature style for this genie."
      className="sm:!max-w-4xl"
    >
      <BrandingEditor
        branding={branding}
        onChange={setBranding}
        isLocked={isPreview ? false : isLocked}
        isPreview={isPreview}
        genieName={genie.name}
        onFixSignatureContacts={isSetupMode || isPreview ? undefined : handleFixSignatureContacts}
        isFixingSignatureContacts={fixSignatureContacts.isPending}
        onActiveTabChange={setActiveTab}
        onSwitchModal={
          isSetupMode || isPreview || !onSwitchModal
            ? undefined
            : (modal) => {
                onClose();
                onSwitchModal(modal);
              }
        }
      />

      {!isPreview && activeTab !== "more" && (
        <div className="flex items-center gap-3 pt-4 pb-2">
          <Button onClick={handleSave} disabled={updateAgent.isPending}>
            {updateAgent.isPending ? "Saving..." : "Save Branding"}
          </Button>
          <Button variant="outline" onClick={handleResetToDefault} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Reset to Default
          </Button>
        </div>
      )}
    </CoreDrawer>
  );
};

export default BrandingModal;
